// File: src/pages/AllMeetingsPage.jsx
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useMeetings } from '../hooks/useMeetings';
import MeetingCard from '../components/MeetingCard';
import { DAYS_OF_WEEK, GENDER_TYPES, MEETING_TYPES, toPersian } from '../data/constants';

/* ── Chip ────────────────────────── */
const Chip = ({ active, color = '#0ea5e9', onClick, children }) => (
  <button onClick={onClick} style={{
    flexShrink: 0, padding: '7px 13px', borderRadius: 12,
    fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
    outline: 'none', transition: 'all .15s',
    border: active ? `1px solid ${color}` : '1px solid var(--border)',
    background: active ? `${color}18` : 'var(--bg2)',
    color: active ? color : 'var(--text2)',
  }}>
    {children}
  </button>
);

/* ── DayBtn ──────────────────────── */
const DayBtn = ({ day, active, onClick }) => (
  <button onClick={onClick} style={{
    flexShrink: 0, width: 38, height: 38, borderRadius: 10,
    fontSize: 11, fontWeight: 950, cursor: 'pointer', transition: 'all .15s',
    border: active ? '1px solid #0ea5e9' : '1px solid var(--border)',
    background: active ? 'rgba(14,165,233,.12)' : 'var(--bg2)',
    color: active ? '#0ea5e9' : 'var(--text3)',
  }}>
    {day.short}
  </button>
);

/* ── LocationSelect — dropdown مکان با آیکون ── */
const LocationSelect = ({ value, onChange, placeholder, icon, options, disabled }) => (
  <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
    <span style={{
      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
      fontSize: 13, pointerEvents: 'none', opacity: disabled ? .3 : .7, zIndex: 1,
    }}>{icon}</span>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: '100%', boxSizing: 'border-box',
        background: 'var(--bg2)', border: `1px solid ${value ? '#0ea5e9' : 'var(--border)'}`,
        borderRadius: 12, padding: '10px 34px 10px 12px',
        fontSize: 12.5, fontWeight: value ? 700 : 500,
        color: value ? 'var(--text)' : 'var(--text3)',
        outline: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? .5 : 1, fontFamily: 'inherit',
        appearance: 'none', WebkitAppearance: 'none',
        transition: 'border-color .15s',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={o.id} value={o.id} style={{ background: 'var(--bg2)', color: 'var(--text)' }}>
          {o.name}{o.count > 0 ? ` (${toPersian(o.count)})` : ''}
        </option>
      ))}
    </select>
    <span style={{
      position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
      fontSize: 9, pointerEvents: 'none', color: 'var(--text3)',
    }}>▼</span>
  </div>
);

/* ── Spinner ─────────────────────── */
const Spinner = () => (
  <div style={{ padding: '44px 0', textAlign: 'center' }}>
    <div style={{
      width: 30, height: 30, margin: '0 auto 12px',
      border: '3px solid var(--border)', borderTop: '3px solid #0ea5e9',
      borderRadius: '50%', animation: 'spin 1s linear infinite',
    }} />
    <p style={{ color: 'var(--text3)', fontSize: 12, fontWeight: 600 }}>در حال دریافت جلسات...</p>
  </div>
);

/* ══════════════════════════════════════════════════ */
export default function AllMeetingsPage() {
  const [type, setType]     = useState('');
  const [gender, setGender] = useState('');
  const [days, setDays]     = useState([]);
  const [zoneId, setZoneId] = useState('');
  const [provId, setProvId] = useState('');
  const [cityId, setCityId] = useState('');
  const [q, setQ]           = useState('');

  const { meetings: allMeetings, loading, error, refresh } = useMeetings({});

  /* ── استخراج ناحیه/استان/شهر از خود داده (مثل داشبورد) ── */
  const { zoneOpts, provOpts, cityOpts } = useMemo(() => {
    const zMap = new Map(), pMap = new Map(), cMap = new Map();
    allMeetings.forEach(m => {
      // ناحیه
      if (m.zone_id && m.zone_name) {
        if (!zMap.has(m.zone_id)) zMap.set(m.zone_id, { id: m.zone_id, name: m.zone_name, count: 0 });
        zMap.get(m.zone_id).count++;
      }
      // استان
      if (m.province_id && m.province_name) {
        if (!pMap.has(m.province_id)) pMap.set(m.province_id, { id: m.province_id, name: m.province_name, count: 0 });
        pMap.get(m.province_id).count++;
      }
      // شهر — فقط شهرهای استان انتخاب‌شده (یا همه اگر استان نیست)
      if (m.city_id && m.city_name) {
        if (!provId || String(m.province_id) === String(provId)) {
          if (!cMap.has(m.city_id)) cMap.set(m.city_id, { id: m.city_id, name: m.city_name, count: 0 });
          cMap.get(m.city_id).count++;
        }
      }
    });
    const sortFn = (a, b) => b.count - a.count;
    return {
      zoneOpts: Array.from(zMap.values()).sort(sortFn),
      provOpts: Array.from(pMap.values()).sort(sortFn),
      cityOpts: Array.from(cMap.values()).sort(sortFn),
    };
  }, [allMeetings, provId]);

  /* ── فیلترینگ ── */
  const filtered = useMemo(() => {
    let list = allMeetings;
    if (type)   list = list.filter(m => m.type === type);
    if (gender) list = list.filter(m => m.gender_type === gender);
    if (days.length > 0) list = list.filter(m => (m.days_array || []).some(d => days.includes(Number(d))));
    if (zoneId) list = list.filter(m => String(m.zone_id) === String(zoneId));
    if (provId) list = list.filter(m => String(m.province_id) === String(provId));
    if (cityId) list = list.filter(m => String(m.city_id) === String(cityId));
    if (q.trim()) {
      const lq = q.trim().toLowerCase();
      list = list.filter(m =>
        (m.title || '').toLowerCase().includes(lq) ||
        (m.address || '').toLowerCase().includes(lq) ||
        (m.location_name || '').toLowerCase().includes(lq) ||
        (m.city_name || '').toLowerCase().includes(lq) ||
        (m.zone_name || '').toLowerCase().includes(lq) ||
        (m.group_rep_phone || '').toLowerCase().includes(lq) ||
        (m.description || '').toLowerCase().includes(lq)
      );
    }
    return list;
  }, [allMeetings, type, gender, days, zoneId, provId, cityId, q]);

  /* ── Infinite Scroll ── */
  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [type, gender, days, zoneId, provId, cityId, q]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;
  const loadMore = useCallback(() => {
    setVisibleCount(c => Math.min(c + PAGE_SIZE, filtered.length));
  }, [filtered.length]);

  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    let busy = false;
    const obs = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !busy) {
          busy = true;
          setTimeout(() => { loadMore(); busy = false; }, 120);
        }
      },
      { rootMargin: '120px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadMore, visibleCount]);

  /* ── کمکی‌ها ── */
  const activeCount = (type ? 1 : 0) + (gender ? 1 : 0) + (days.length > 0 ? 1 : 0)
    + (zoneId ? 1 : 0) + (provId ? 1 : 0) + (cityId ? 1 : 0);
  const clearAll = () => {
    setType(''); setGender(''); setDays([]); setZoneId(''); setProvId(''); setCityId(''); setQ('');
  };
  const toggleDay = id => setDays(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);

  return (
    <div style={{ direction: 'rtl' }}>

      {/* 🔍 جستجو */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 15, pointerEvents: 'none' }}>🔍</span>
        <input
          type="text"
          placeholder="جستجو: عنوان، آدرس، شهر، نماینده..."
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{
            width: '100%', boxSizing: 'border-box', background: 'var(--bg2)',
            border: '1px solid var(--border)', borderRadius: 14,
            padding: '11px 42px 11px 14px', fontSize: 13, color: 'var(--text)', outline: 'none',
            transition: 'border-color 0.2s',
          }}
          onFocus={e => e.target.style.borderColor = '#0ea5e9'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
        {q && (
          <button onClick={() => setQ('')} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 16, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        )}
      </div>

      {/* 🏷️ چیپ‌های نوع و جنسیت */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 10 }} className="no-scroll">
        {MEETING_TYPES.map(t => (
          <Chip key={t.id} active={type === t.id} color='#10b981' onClick={() => setType(type === t.id ? '' : t.id)}>
            {t.icon} {t.name}
          </Chip>
        ))}
        <div style={{ width: 1, background: 'var(--border)', flexShrink: 0, margin: '4px 0' }} />
        {GENDER_TYPES.map(g => (
          <Chip key={g.id} active={gender === g.id} color={g.color} onClick={() => setGender(gender === g.id ? '' : g.id)}>
            {g.icon} {g.name}
          </Chip>
        ))}
        {activeCount > 0 && (
          <Chip active color='#ef4444' onClick={clearAll}>✕ حذف ({toPersian(activeCount)})</Chip>
        )}
      </div>

      {/* 📅 روزهای هفته */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 12, alignItems: 'center', overflowX: 'auto', paddingBottom: 2 }} className="no-scroll">
        <button onClick={() => setDays([])} style={{
          padding: '7px 12px', borderRadius: 10, fontSize: 11, fontWeight: 950, cursor: 'pointer', transition: 'all .15s', flexShrink: 0,
          border: days.length === 0 ? '1px solid #0ea5e9' : '1px solid var(--border)',
          background: days.length === 0 ? 'rgba(14,165,233,.12)' : 'var(--bg2)',
          color: days.length === 0 ? '#0ea5e9' : 'var(--text3)',
        }}>همه</button>
        {DAYS_OF_WEEK.map(d => (
          <DayBtn key={d.id} day={d} active={days.includes(d.id)} onClick={() => toggleDay(d.id)} />
        ))}
      </div>

      {/* 🗺️ فیلتر مکان: ناحیه → استان → شهر (مستقیم) */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <LocationSelect
          value={zoneId} onChange={setZoneId}
          placeholder="🔲 ناحیه" icon="🔲" options={zoneOpts}
        />
        <LocationSelect
          value={provId}
          onChange={v => { setProvId(v); setCityId(''); }}
          placeholder="🗺️ استان" icon="🗺️" options={provOpts}
        />
        <LocationSelect
          value={cityId} onChange={setCityId}
          placeholder="🏙️ شهر" icon="🏙️" options={cityOpts}
          disabled={!cityOpts.length}
        />
      </div>

      {/* ── نوار نتایج ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, padding: '0 2px' }}>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>
          {loading ? '⏳ بارگذاری...' : (
            <span>یافت شده: <strong style={{ color: 'var(--text)', fontWeight: 900 }}>{toPersian(filtered.length)}</strong> جلسه</span>
          )}
        </div>
        <button onClick={refresh} style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}>🔄 بروزرسانی</button>
      </div>

      {/* خطا */}
      {error && (
        <div style={{ background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.15)', borderRadius: 12, padding: 14, marginBottom: 14, textAlign: 'center' }}>
          <p style={{ color: '#f87171', fontSize: 13, marginBottom: 8, fontWeight: 600 }}>⚠️ {error}</p>
          <button onClick={refresh} style={{ fontSize: 12, color: '#ef4444', fontWeight: 800, background: 'none', border: 'none', cursor: 'pointer' }}>تلاش مجدد</button>
        </div>
      )}

      {/* لیست کارت‌ها با infinite scroll */}
      {loading && !allMeetings.length ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <div style={{ padding: '60px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 38, marginBottom: 12 }}>{q ? '🔍' : '📭'}</div>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 14, fontWeight: 600 }}>
            {q ? `نتیجه‌ای برای «${q}» پیدا نشد` : 'جلسه‌ای با این فیلترها یافت نشد'}
          </p>
          {activeCount > 0 && (
            <button onClick={clearAll} style={{ fontSize: 12, color: '#0ea5e9', fontWeight: 800, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 20, padding: '8px 18px', cursor: 'pointer' }}>
              پاک کردن فیلترها
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visible.map((m, i) => (
              <div key={m.id} style={{ animation: i >= visibleCount - PAGE_SIZE ? `slideUp .3s ease ${Math.min(i % PAGE_SIZE, 8) * 0.03}s both` : 'none' }}>
                <MeetingCard meeting={{ ...m, todayStartTime: m.todayStartTime || m.first_start_time }} />
              </div>
            ))}
          </div>

          {hasMore && (
            <div ref={sentinelRef} style={{ padding: '16px 0', textAlign: 'center' }}>
              <button onClick={loadMore} style={{
                background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 20,
                padding: '10px 24px', cursor: 'pointer', fontSize: 13, fontWeight: 800,
                color: '#0ea5e9', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ width: 16, height: 16, border: '2px solid var(--border)', borderTop: '2px solid #0ea5e9', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
                نمایش بیشتر
              </button>
              <p style={{ color: 'var(--text3)', fontSize: 11, marginTop: 8 }}>
                {toPersian(visible.length)} از {toPersian(filtered.length)} جلسه
              </p>
            </div>
          )}

          {!hasMore && filtered.length > PAGE_SIZE && (
            <div style={{ padding: '20px 0 8px', textAlign: 'center' }}>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>✓ همه {toPersian(filtered.length)} جلسه نمایش داده شد</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
